import { useLingui } from "@lingui/react";
import { Select } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { sortBy } from "lodash-es";

import {
  getGetBookingQueryKey,
  getGetBookingsQueryKey,
  useChangeBookingStatus,
} from "@/shared/api/generated/booking";
import {
  GetBookingResponse,
  GetBookingsItem,
  GetBookingsItemStatus,
  GetBookingsResponse,
} from "@/shared/api/generated/schemas";
import {
  GetFieldsBySubjectKind,
  getSubject,
} from "@/shared/providers/casl-provider/authorization/subjects";
import { useAppAbility } from "@/shared/providers/casl-provider/use-app-ability";

import { bookingStatusOptions } from "../utils/status";

export default function StatusSelector({
  booking,
}: {
  booking: GetBookingsItem;
}) {
  const { i18n } = useLingui();

  const queryClient = useQueryClient();

  const { mutate: changeBookingStatus } = useChangeBookingStatus({
    mutation: {
      onSuccess: (data) => {
        queryClient.setQueryData<GetBookingResponse>(
          getGetBookingQueryKey(booking.id),
          (prev) => ({
            ...prev,
            data: { ...prev?.data, ...data.data },
          }),
        );
        // Update this booking in the for GET /bookings
        queryClient.setQueriesData<GetBookingsResponse>(
          { queryKey: getGetBookingsQueryKey() },
          (old) => ({
            ...old,
            data: (old?.data ?? []).map((booking) =>
              booking.id === data.data.id ? data.data : booking,
            ),
          }),
        );
      },
    },
  });

  const ability = useAppAbility();

  const canUpdateBookingStatus = ability.can(
    "update",
    getSubject("Booking", booking),
    "status" satisfies GetFieldsBySubjectKind<"Booking">,
  );

  return (
    <Select
      value={booking.status}
      data={sortBy(bookingStatusOptions(i18n), (o) => o.label.toLowerCase())}
      onChange={(value) => {
        const newStatus = value as GetBookingsItemStatus | null;

        if (newStatus) {
          changeBookingStatus({
            id: booking.id,
            data: {
              status: newStatus,
              updatedAt: booking.updatedAt,
            },
          });
        }
      }}
      searchable
      readOnly={!canUpdateBookingStatus}
    />
  );
}
